import { useQueryClient } from '@tanstack/react-query';
import { CatenaLinkedFleetStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CatenaLinkedFleet } from '~hooks/useCatena/models';
import useCatena from '~hooks/useCatena/useCatena';
import useCatenaLinkedFleetsQuery, {
  CATENA_LINKED_FLEETS_QUERY_KEY,
  catenaLinkedFleetsQueryKey,
} from '~queries/useCatenaLinkedFleetsQuery';
import { Paginated, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { Alert, alert, AlertTypes } from '~types/AlertTypes';
import usePopupSession from '~utils/hooks/usePopupSession';

const POLL_INITIAL_INTERVAL_MS = 3000;
const POLL_MAX_INTERVAL_MS = 15000;
const POLL_BACKOFF_FACTOR = 1.5;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const TERMINAL_STATUSES: ReadonlyArray<CatenaLinkedFleetStatus> = [
  CatenaLinkedFleetStatus.CONNECTED,
  CatenaLinkedFleetStatus.DECLINED,
  CatenaLinkedFleetStatus.EXPIRED,
  CatenaLinkedFleetStatus.DISCONNECTED,
];

const TERMINAL_STATUS_TOASTS: Partial<
  Record<CatenaLinkedFleetStatus, { kind: Alert['kind']; messageKey: string }>
> = {
  [CatenaLinkedFleetStatus.CONNECTED]: {
    kind: AlertTypes.success,
    messageKey: 'administration.integration.catena.toast.connected',
  },
  [CatenaLinkedFleetStatus.DECLINED]: {
    kind: AlertTypes.error,
    messageKey: 'administration.integration.catena.toast.declined',
  },
  [CatenaLinkedFleetStatus.EXPIRED]: {
    kind: AlertTypes.warn,
    messageKey: 'administration.integration.catena.toast.expired',
  },
};

const haveSameIdsAndStatuses = (a: CatenaLinkedFleet[], b: CatenaLinkedFleet[]) =>
  a.length === b.length &&
  a.every((fleet, index) => fleet.id === b[index].id && fleet.status === b[index].status);

interface LinkedFleetsPage {
  cursor?: string;
  direction?: PaginationLink;
}

export default function useCatenaConnect({ cursor, direction }: LinkedFleetsPage = {}) {
  const { toasterStore, userStore } = useStores();
  const { createLinkedFleet, disconnectLinkedFleet, listLinkedFleets } = useCatena();
  const { openBlankPopup } = usePopupSession();
  const queryClient = useQueryClient();

  const fleetRef = userStore.userCompany?.id ?? '';

  const { isError, isLoading, linkedFleets, pagination } = useCatenaLinkedFleetsQuery({
    companyId: fleetRef,
    cursor,
    direction,
  });
  const [pendingFleetId, setPendingFleetId] = useState<string | null>(null);

  const popupRef = useRef<Window | null>(null);

  const stopPending = useCallback(() => {
    setPendingFleetId(null);
    popupRef.current?.close();
    popupRef.current = null;
  }, []);

  useEffect(() => {
    if (!pendingFleetId) return;

    const controller = new AbortController();
    const { signal } = controller;
    let timeoutId: number | undefined;
    let interval = POLL_INITIAL_INTERVAL_MS;
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      let firstPage: Awaited<ReturnType<typeof listLinkedFleets>>;
      try {
        firstPage = await listLinkedFleets({ signal });
      } catch (error) {
        if (signal.aborted) return;
        throw error;
      }
      if (signal.aborted) return;

      if (firstPage) {
        const fleets = firstPage.data;
        // Catena may briefly omit a new fleet; keep its optimistic row until settled.
        queryClient.setQueryData<Paginated<CatenaLinkedFleet>>(
          catenaLinkedFleetsQueryKey(fleetRef),
          (prev) => {
            const prevFleets = prev?.data ?? [];
            const optimistic = fleets.some((f) => f.id === pendingFleetId)
              ? undefined
              : prevFleets.find((f) => f.id === pendingFleetId);
            const next = optimistic ? [optimistic, ...fleets] : fleets;

            return {
              ...firstPage,
              data: haveSameIdsAndStatuses(prevFleets, next) ? prevFleets : next,
            };
          },
        );

        const pendingStatus = fleets.find((f) => f.id === pendingFleetId)?.status;

        if (pendingStatus && TERMINAL_STATUSES.includes(pendingStatus)) {
          stopPending();

          const toast = TERMINAL_STATUS_TOASTS[pendingStatus];
          if (toast) {
            toasterStore.push(alert(t(toast.messageKey), toast.kind));
          }
          return;
        }
      }

      if (Date.now() >= deadline) {
        stopPending();
        return;
      }

      interval = Math.min(interval * POLL_BACKOFF_FACTOR, POLL_MAX_INTERVAL_MS);
      timeoutId = window.setTimeout(poll, interval);
    };

    timeoutId = window.setTimeout(poll, interval);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [pendingFleetId, fleetRef, listLinkedFleets, queryClient, stopPending]);

  const hasPendingFleet = pendingFleetId !== null;

  const connect = useCallback(async () => {
    if (!fleetRef || hasPendingFleet) return false;

    const popup = openBlankPopup(
      t('administration.integration.catena.toast.popup_blocked'),
    );

    const created = await createLinkedFleet({ fleet_ref: fleetRef });
    if (!created) {
      popup?.close();
      return false;
    }

    queryClient.setQueryData<Paginated<CatenaLinkedFleet>>(
      catenaLinkedFleetsQueryKey(fleetRef),
      (prev) => prev && { ...prev, data: [created, ...prev.data] },
    );

    if (popup && created.magicLinkUrl) {
      popup.location.href = created.magicLinkUrl;
      popupRef.current = popup;
    } else {
      popup?.close();
    }

    setPendingFleetId(created.id);
    return true;
  }, [fleetRef, hasPendingFleet, createLinkedFleet, openBlankPopup, queryClient]);

  const disconnect = useCallback(
    async (fleetId: string) => {
      if (!(await disconnectLinkedFleet(fleetId))) return false;

      const companyKey = [CATENA_LINKED_FLEETS_QUERY_KEY, fleetRef];
      queryClient.setQueriesData<Paginated<CatenaLinkedFleet>>(
        { queryKey: companyKey },
        (prev) => prev && { ...prev, data: prev.data.filter(({ id }) => id !== fleetId) },
      );

      if (fleetId === pendingFleetId) {
        stopPending();
      }

      // A running poll refreshes the first page itself and keeps its optimistic row.
      if (!pendingFleetId || fleetId === pendingFleetId) {
        void queryClient.invalidateQueries({ queryKey: companyKey });
      }
      return true;
    },
    [disconnectLinkedFleet, fleetRef, pendingFleetId, queryClient, stopPending],
  );

  return {
    canConnect: Boolean(fleetRef) && !isLoading && !isError && !hasPendingFleet,
    connect,
    disconnect,
    isLoading,
    linkedFleets,
    pagination,
    pendingFleetId,
    stopPending,
  };
}
